import React, { useMemo } from "react";
import { Check } from "lucide-react";
import { AL_LOCATIONS, AL_QARQE_ORDERED, COUNTRIES, findQarkByLabel, findBashkiByLabel } from "../lib/al_locations";
import { toast } from "sonner";

/**
 * Structured address form with **cascading** dropdowns:
 *   Country (dropdown, only Albania enabled — rest greyed)
 *     → State / Qark (dropdown of 12 qarqe, enabled only when Albania selected)
 *       → City / Bashkia (dropdown filtered by qark)
 *
 * Postal code + Road/landmark remain free text.
 *
 * Props:
 *   value: { country, state, municipality, locality, postal_code, address, address_lat, address_lng }
 *   onChange(partial): merges into value
 *   testIdPrefix: defaults to "addr-"
 */
const StructuredAddressForm = ({ value, onChange, testIdPrefix = "addr-" }) => {
    const set = (patch) => onChange({ ...value, ...patch });

    // Normalise the incoming state value into a canonical qark id (Google reverse-geocode
    // may give us "Tirana" / "Tiranë" / "tirane" — we accept all and snap to the qark dropdown).
    const currentQark = useMemo(() => {
        if (!value.state) return null;
        return findQarkByLabel(value.state) || null;
    }, [value.state]);

    const bashkiOptions = currentQark ? AL_LOCATIONS[currentQark.id].bashki : [];

    // Similarly for the current bashkia
    const currentBashki = useMemo(() => {
        if (!currentQark || !value.municipality) return null;
        return findBashkiByLabel(currentQark.id, value.municipality) || null;
    }, [currentQark, value.municipality]);

    const countryEnabled = !!COUNTRIES.find(
        (c) => c.name.toLowerCase() === (value.country || "").toLowerCase() && c.enabled,
    );

    const handleCountryChange = (e) => {
        const target = COUNTRIES.find((c) => c.iso === e.target.value);
        if (!target) return;
        if (!target.enabled) {
            toast.error(`Sorry, we don't deliver to ${target.name} yet.`);
            return;
        }
        // Switching country resets the cascade
        set({ country: target.name, state: "", municipality: "" });
    };

    const handleQarkChange = (e) => {
        const qarkId = e.target.value;
        const q = AL_LOCATIONS[qarkId];
        // Resetting bashkia because the previous one may not belong to the new qark.
        set({ state: q ? q.label : "", municipality: "" });
    };

    const handleBashkiChange = (e) => {
        const bid = e.target.value;
        const b = currentQark?.bashki.find((x) => x.id === bid);
        set({ municipality: b ? b.label : "" });
    };

    // Current ISO code for the country dropdown's value attribute
    const currentCountryIso = useMemo(() => {
        const c = COUNTRIES.find((x) => x.name.toLowerCase() === (value.country || "").toLowerCase());
        return c?.iso || "AL";
    }, [value.country]);

    return (
        <div className="space-y-3" data-testid="structured-address-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-1.5">
                        Country *
                    </label>
                    <select
                        required
                        value={currentCountryIso}
                        onChange={handleCountryChange}
                        data-testid={`${testIdPrefix}country`}
                        className="w-full h-11 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm font-medium text-neutral-800"
                    >
                        {COUNTRIES.map((c) => (
                            <option
                                key={c.iso}
                                value={c.iso}
                                disabled={!c.enabled}
                            >
                                {c.flag} {c.name}{c.enabled ? "" : " — coming soon"}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-1.5">
                        State / Qark *
                    </label>
                    <select
                        required
                        value={currentQark?.id || ""}
                        onChange={handleQarkChange}
                        disabled={!countryEnabled}
                        data-testid={`${testIdPrefix}state`}
                        className="w-full h-11 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm font-medium text-neutral-800 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <option value="" disabled>
                            {countryEnabled ? "Select a state…" : "Select a country first"}
                        </option>
                        {AL_QARQE_ORDERED.map((id) => (
                            <option key={id} value={id}>
                                {AL_LOCATIONS[id].label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-1.5">
                        City / Bashkia *
                    </label>
                    <select
                        required
                        value={currentBashki?.id || ""}
                        onChange={handleBashkiChange}
                        disabled={!currentQark}
                        data-testid={`${testIdPrefix}municipality`}
                        className="w-full h-11 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm font-medium text-neutral-800 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <option value="" disabled>
                            {currentQark ? "Select a city…" : "Select a state first"}
                        </option>
                        {bashkiOptions.map((b) => (
                            <option key={b.id} value={b.id}>
                                {b.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-1.5">
                        AREA (LAGJE), LANDMARK / (PRANË/AFËR)
                    </label>
                    <input
                        type="text"
                        value={value.locality || ""}
                        onChange={(e) => set({ locality: e.target.value })}
                        placeholder="Blloku, Pranë Shkolla"
                        data-testid={`${testIdPrefix}locality`}
                        className="w-full h-11 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                    />
                </div>
            </div>

            <div className="hidden">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-1.5">
                    Pin code / Postal code
                </label>
                <input
                    type="text"
                    maxLength={10}
                    value={value.postal_code || ""}
                    onChange={(e) => set({ postal_code: e.target.value.slice(0, 10) })}
                    placeholder="1001"
                    data-testid={`${testIdPrefix}postal`}
                    className="w-full h-11 px-3 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm"
                />
            </div>

            <div className="hidden">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-1.5">
                    Road + nearby landmark
                </label>
                <textarea
                    rows={2}
                    value={value.address || ""}
                    onChange={(e) => set({ address: e.target.value })}
                    placeholder="e.g., Rruga e Durrësit 23, prapa Hotel Tirana International"
                    data-testid={`${testIdPrefix}address`}
                    className="w-full px-3 py-2.5 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-[#FF5722] outline-none text-sm resize-none"
                />
            </div>

            {value.address_lat != null && (
                <div className="text-[11px] text-emerald-600 inline-flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Location confirmed ({Number(value.address_lat).toFixed(4)}, {Number(value.address_lng).toFixed(4)})
                </div>
            )}
        </div>
    );
};

export default StructuredAddressForm;
